/**
 * Word targets — the drafter's length budget for one matter.
 *
 * Three modes, matching the backend contract:
 *   inherit  the section catalogue decides (the default; nothing is
 *            imposed on a matter nobody has configured)
 *   overall  one document budget, spread across the body sections in
 *            proportion to the catalogue
 *   section  each section carries its own target
 *
 * A per-section number always wins, whatever the mode — the mode only
 * decides what happens to the sections left blank. That is why the
 * per-section table stays visible in every mode, with the effective
 * range shown next to each row so the drafter can see the consequence
 * of the mode without doing the arithmetic.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import {
  getSpecWordTargets,
  patchSpecWordTargets,
  type SpecWordTargetMode,
  type SpecWordTargetsPatchBody,
  type SpecWordTargetsResponse,
} from '@/api/client';

interface Props {
  matterId: string;
  onClose: () => void;
  /** Called after a successful save so the caller can refresh any
   * length badges it renders. */
  onSaved?: (next: SpecWordTargetsResponse) => void;
}

type Draft = Record<string, { min: number | ''; max: number | '' }>;

const MODE_COPY: Record<SpecWordTargetMode, { label: string; hint: string }> = {
  inherit: {
    label: 'Standard lengths',
    hint: 'Each section uses the built-in target for its kind.',
  },
  overall: {
    label: 'One target for the document',
    hint: 'Spread across the body sections in proportion to their usual length.',
  },
  section: {
    label: 'Set each section',
    hint: 'Sections you leave blank keep their built-in target.',
  },
};

function draftFromResponse(res: SpecWordTargetsResponse): Draft {
  const out: Draft = {};
  for (const s of res.sections) {
    out[s.section_id] = {
      min: s.target_word_count_min ?? '',
      max: s.target_word_count_max ?? '',
    };
  }
  return out;
}

function numberOrNull(value: number | ''): number | null {
  return value === '' ? null : value;
}

export default function WordTargetsDialog({
  matterId, onClose, onSaved,
}: Props): JSX.Element {
  const [data, setData] = useState<SpecWordTargetsResponse | null>(null);
  const [mode, setMode] = useState<SpecWordTargetMode>('inherit');
  const [overall, setOverall] = useState<{ min: number | ''; max: number | '' }>(
    { min: '', max: '' },
  );
  const [draft, setDraft] = useState<Draft>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hydrate = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getSpecWordTargets(matterId);
      setData(res);
      setMode(res.mode);
      setOverall({ min: res.overall.min ?? '', max: res.overall.max ?? '' });
      setDraft(draftFromResponse(res));
    } catch (e: unknown) {
      setError(readError(e, 'Could not load word targets.'));
    } finally {
      setLoading(false);
    }
  }, [matterId]);

  useEffect(() => { void hydrate(); }, [hydrate]);

  const dirty = useMemo(() => {
    if (!data) return false;
    if (mode !== data.mode) return true;
    if ((data.overall.min ?? '') !== overall.min) return true;
    if ((data.overall.max ?? '') !== overall.max) return true;
    return data.sections.some((s) => {
      const row = draft[s.section_id];
      if (!row) return false;
      return (s.target_word_count_min ?? '') !== row.min
        || (s.target_word_count_max ?? '') !== row.max;
    });
  }, [data, mode, overall, draft]);

  const rangeError = useMemo(() => {
    if (overall.min !== '' && overall.max !== '' && overall.min > overall.max) {
      return 'The document minimum is above its maximum.';
    }
    for (const s of data?.sections ?? []) {
      const row = draft[s.section_id];
      if (!row) continue;
      if (row.min !== '' && row.max !== '' && row.min > row.max) {
        return `${s.title || s.section_kind}: minimum is above maximum.`;
      }
    }
    return null;
  }, [data, overall, draft]);

  const handleSave = useCallback(async () => {
    if (!data || saving || !dirty || rangeError) return;
    setSaving(true);
    setError(null);
    try {
      const body: SpecWordTargetsPatchBody = { mode };
      if (overall.min !== '') body.overall_min = overall.min;
      if (overall.max !== '') body.overall_max = overall.max;
      const changed = data.sections
        .filter((s) => {
          const row = draft[s.section_id];
          if (!row) return false;
          return (s.target_word_count_min ?? '') !== row.min
            || (s.target_word_count_max ?? '') !== row.max;
        })
        .map((s) => ({
          section_id: s.section_id,
          target_word_count_min: numberOrNull(draft[s.section_id].min),
          target_word_count_max: numberOrNull(draft[s.section_id].max),
        }));
      if (changed.length > 0) body.sections = changed;
      const next = await patchSpecWordTargets(matterId, body);
      setData(next);
      setMode(next.mode);
      setOverall({ min: next.overall.min ?? '', max: next.overall.max ?? '' });
      setDraft(draftFromResponse(next));
      onSaved?.(next);
      onClose();
    } catch (e: unknown) {
      setError(readError(e, 'Save failed.'));
    } finally {
      setSaving(false);
    }
  }, [
    data, saving, dirty, rangeError, mode, overall, draft, matterId,
    onSaved, onClose,
  ]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-6"
      data-testid="spec-word-targets-dialog"
    >
      <div className="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-sm border border-zinc-200 bg-white shadow-lg">
        <div className="flex items-center justify-between border-b border-zinc-200 px-5 py-3">
          <div>
            <h2 className="text-sm font-semibold text-zinc-900">Word targets</h2>
            <p className="text-xs text-zinc-500">
              How long this specification should be.
              {data?.jurisdiction ? ` Defaults come from ${data.jurisdiction}.` : ''}
            </p>
          </div>
          <button
            type="button"
            className="rounded-sm p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
            onClick={onClose}
            aria-label="Close"
            data-testid="spec-word-targets-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading ? (
            <div className="flex items-center gap-2 py-8 text-sm text-zinc-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading…
            </div>
          ) : (
            <>
              <fieldset className="mb-4">
                <legend className="sr-only">Target mode</legend>
                <div className="space-y-2">
                  {(Object.keys(MODE_COPY) as SpecWordTargetMode[]).map((key) => (
                    <label
                      key={key}
                      className="flex cursor-pointer items-start gap-2 text-sm"
                    >
                      <input
                        type="radio"
                        className="mt-1"
                        name="word-target-mode"
                        checked={mode === key}
                        onChange={() => setMode(key)}
                        data-testid={`spec-word-targets-mode-${key}`}
                      />
                      <span>
                        <span className="font-medium text-zinc-800">
                          {MODE_COPY[key].label}
                        </span>
                        <span className="block text-xs text-zinc-500">
                          {MODE_COPY[key].hint}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>

              {mode === 'overall' && (
                <div
                  className="mb-4 flex items-center gap-3 rounded-sm border border-zinc-200 bg-zinc-50 p-3"
                  data-testid="spec-word-targets-overall"
                >
                  <span className="text-xs font-medium text-zinc-600">
                    Whole document
                  </span>
                  <label className="flex items-center gap-2">
                    <span className="text-[11px] text-zinc-500">min</span>
                    <input
                      type="number"
                      min={1}
                      className="h-8 w-28 rounded-sm border border-zinc-300 px-2 text-sm"
                      value={overall.min}
                      onChange={(e) => setOverall((o) => ({
                        ...o,
                        min: e.target.value === '' ? '' : parseInt(e.target.value, 10) || '',
                      }))}
                      data-testid="spec-word-targets-overall-min"
                    />
                  </label>
                  <label className="flex items-center gap-2">
                    <span className="text-[11px] text-zinc-500">max</span>
                    <input
                      type="number"
                      min={1}
                      className="h-8 w-28 rounded-sm border border-zinc-300 px-2 text-sm"
                      value={overall.max}
                      onChange={(e) => setOverall((o) => ({
                        ...o,
                        max: e.target.value === '' ? '' : parseInt(e.target.value, 10) || '',
                      }))}
                      data-testid="spec-word-targets-overall-max"
                    />
                  </label>
                </div>
              )}

              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-200 text-left text-[11px] uppercase tracking-wide text-zinc-500">
                    <th className="py-2">Section</th>
                    <th className="py-2 w-24">Min</th>
                    <th className="py-2 w-24">Max</th>
                    <th className="py-2 w-40 text-right">In force</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.sections ?? [])
                    .filter((s) => !s.is_auto_generated)
                    .map((s) => {
                      const row = draft[s.section_id] ?? { min: '', max: '' };
                      return (
                        <tr
                          key={s.section_id}
                          className="border-b border-zinc-100 last:border-b-0"
                          data-testid={`spec-word-targets-row-${s.section_kind}`}
                        >
                          <td className="py-2 pr-2">
                            <span className="text-zinc-800">{s.title || s.section_kind}</span>
                            <span className="block text-[11px] text-zinc-400">
                              {s.word_count.toLocaleString()} words now
                            </span>
                          </td>
                          <td className="py-2 pr-2">
                            <input
                              type="number"
                              min={1}
                              className="h-8 w-20 rounded-sm border border-zinc-300 px-2 text-sm disabled:bg-zinc-100"
                              value={row.min}
                              disabled={!!s.guidance}
                              placeholder={s.effective_min ? String(s.effective_min) : '—'}
                              onChange={(e) => setDraft((d) => ({
                                ...d,
                                [s.section_id]: {
                                  ...row,
                                  min: e.target.value === '' ? '' : parseInt(e.target.value, 10) || '',
                                },
                              }))}
                              data-testid={`spec-word-targets-min-${s.section_kind}`}
                            />
                          </td>
                          <td className="py-2 pr-2">
                            <input
                              type="number"
                              min={1}
                              className="h-8 w-20 rounded-sm border border-zinc-300 px-2 text-sm disabled:bg-zinc-100"
                              value={row.max}
                              disabled={!!s.guidance}
                              placeholder={s.effective_max ? String(s.effective_max) : '—'}
                              onChange={(e) => setDraft((d) => ({
                                ...d,
                                [s.section_id]: {
                                  ...row,
                                  max: e.target.value === '' ? '' : parseInt(e.target.value, 10) || '',
                                },
                              }))}
                              data-testid={`spec-word-targets-max-${s.section_kind}`}
                            />
                          </td>
                          <td className="py-2 text-right text-[11px] text-zinc-500">
                            {s.guidance
                              ? s.guidance
                              : s.effective_min && s.effective_max
                                ? `${s.effective_min.toLocaleString()}–${s.effective_max.toLocaleString()} words`
                                : '—'}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
              <p className="mt-3 text-[11px] text-zinc-400">
                A number you type here always wins. Blank rows follow the
                mode above. Sections whose length is fixed by convention
                (the title, the abstract, one sentence per figure) are
                not editable.
              </p>
            </>
          )}
          {(error || rangeError) && (
            <p
              className="mt-3 rounded-sm border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
              data-testid="spec-word-targets-error"
            >
              {error || rangeError}
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-zinc-200 px-5 py-3">
          <button
            type="button"
            className="h-8 rounded-sm border border-zinc-300 px-3 text-sm text-zinc-700 hover:bg-zinc-50"
            onClick={onClose}
            data-testid="spec-word-targets-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            className="h-8 rounded-sm bg-zinc-900 px-3 text-sm text-white disabled:opacity-40"
            onClick={() => { void handleSave(); }}
            disabled={!dirty || saving || !!rangeError || loading}
            data-testid="spec-word-targets-save"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function readError(e: unknown, fallback: string): string {
  const detail = (e as {
    response?: { data?: { detail?: string | { detail?: string } } };
    message?: string;
  })?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object' && typeof detail.detail === 'string') {
    return detail.detail;
  }
  return (e as { message?: string })?.message ?? fallback;
}
